import { useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/icons-material';
import {
  DASHBOARD_CHART_PERMISSIONS,
  DASHBOARD_KEY_INDICATORS_PERMISSIONS,
  hasAnyPermission,
} from '~/lib/permissions';
import { DashboardChartsSection } from './components/charts/DashboardChartsSection';
import { DashboardFilters } from './components/DashboardFilters';
import { KpiGrid } from './components/KpiGrid';
import { useKpisQuery } from './hooks/useDashboardAnalytics';
import { useEffectiveRegionalCode } from './hooks/useEffectiveRegionalCode';
import { DEFAULT_DASHBOARD_FILTERS, type DashboardFilterState } from './types';

export function AnalyticsDashboard() {
  const [filters, setFilters] = useState<DashboardFilterState>(DEFAULT_DASHBOARD_FILTERS);
  const { effectiveRegionalCode, canPickRegion, permissionSet } = useEffectiveRegionalCode(
    filters.regionalCode
  );

  const hasKpiPermission = hasAnyPermission(permissionSet, DASHBOARD_KEY_INDICATORS_PERMISSIONS);
  const hasChartPermission = hasAnyPermission(permissionSet, DASHBOARD_CHART_PERMISSIONS);

  const {
    data: kpiData,
    isLoading: kpiLoading,
    errorMessage: kpiError,
  } = useKpisQuery(
    {
      dateRangePreset: filters.dateRangePreset,
      regionalCode: effectiveRegionalCode,
    },
    { enabled: hasKpiPermission }
  );

  return (
    <Box sx={{ width: '100%' }}>
      <DashboardFilters
        filters={filters}
        onFiltersChange={setFilters}
        showRegionalFilter={canPickRegion}
        showDateRangeFilter={hasKpiPermission}
      />

      {hasKpiPermission ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <BarChart color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Key Performance Indicators
            </Typography>
          </Stack>

          {!kpiLoading && kpiError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {kpiError}
            </Alert>
          ) : null}

          <KpiGrid data={kpiData ?? null} loading={kpiLoading} />
        </>
      ) : null}

      {hasChartPermission ? (
        <DashboardChartsSection
          regionalCode={effectiveRegionalCode}
          permissionSet={permissionSet}
        />
      ) : null}
    </Box>
  );
}
